import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance } from '../services/api';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [invitationError, setInvitationError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInvitationError(false);
    setLoading(true);

    try {
      await axiosInstance.post('/account/auth/forgot-password/', { email });
      setSuccess(true);
    } catch (err) {
      const data = err.response?.data;
      if (data?.invitation_not_accepted) {
        setInvitationError(true);
        setError(data.error);
      } else {
        setError(data?.error || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-300">
          {/* Header */}
          <div className="bg-[#556B2F] text-white p-6 text-center">
            <h1 className="text-2xl font-bold mb-2">Reset Your Password</h1>
            <p className="text-base opacity-90">We'll send you a link to get back in</p>
          </div>

          {/* Content */}
          <div className="p-8">
            {success ? (
              /* Success State */
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800">Check Your Email</h2>
                <p className="text-gray-600 text-base">
                  If an account exists with <strong>{email}</strong>, you'll receive a password reset link shortly.
                </p>
                <p className="text-sm text-gray-500">
                  Don't see it? Check your spam folder.
                </p>
                <div className="pt-4">
                  <Link
                    to="/login"
                    className="text-[#556B2F] font-medium hover:underline text-base"
                  >
                    Back to Login
                  </Link>
                </div>
              </div>
            ) : invitationError ? (
              /* Invitation Not Accepted State */
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800">Invitation Pending</h2>
                <p className="text-gray-600 text-base">{error}</p>
                <p className="text-sm text-gray-500">
                  Can't find the invitation email? Check your spam folder or ask the family member who invited you to resend it.
                </p>
                <div className="pt-4">
                  <Link
                    to="/login"
                    className="text-[#556B2F] font-medium hover:underline text-base"
                  >
                    Back to Login
                  </Link>
                </div>
              </div>
            ) : (
              /* Form State */
              <>
                <form className="mt-4 space-y-6" onSubmit={handleSubmit}>
                  {error && (
                    <div className="text-red-500 text-sm text-center">{error}</div>
                  )}
                  <div>
                    <label htmlFor="email-address" className="block text-base font-bold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      id="email-address"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-lg focus:border-[#556B2F] focus:outline-none"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#556B2F] text-white border-none py-4 px-4 text-lg font-bold rounded-lg cursor-pointer transition-colors hover:bg-[#4A5D23] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  </div>
                </form>
                <div className="text-sm text-center mt-4">
                  <Link to="/login" className="font-medium text-[#556B2F] hover:underline">
                    Back to Login
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
